import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Clock, CheckCircle, XCircle, AlertCircle, Plus, Send } from "lucide-react";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

export default function TalkToExpert() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConcern, setSelectedConcern] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [childName, setChildName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const childResponse = await api.get(`/children/profiles/${childId}/`);
      setChildName(childResponse.data.first_name);

      const concernsResponse = await api.get(`/children/expert-requests/?child=${childId}`);
      setConcerns(concernsResponse.data.sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      ));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitConcern = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) return;

    try {
      setSubmitting(true);
      const response = await api.post('/children/expert-requests/', {
        child: childId,
        description: formData.description,
      });

      setConcerns(prev => [response.data, ...prev]);
      setFormData({ title: "", description: "", category: "" });
      setShowAddModal(false);
    } catch (error) {
      console.error("Error submitting concern:", error);
      alert("Failed to submit concern. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCardClick = (concern) => {
    setSelectedConcern(concern);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedConcern(null), 300);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setFormData({ title: "", description: "", category: "" });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="w-5 h-5" />;
      case "in_progress":
        return <AlertCircle className="w-5 h-5" />;
      case "completed":
        return <CheckCircle className="w-5 h-5" />;
      case "cancelled":
        return <XCircle className="w-5 h-5" />;
      default:
        return <AlertCircle className="w-5 h-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "in_progress":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "completed":
        return "bg-green-100 text-green-700 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-700 border-red-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "pending":
        return "Pending";
      case "in_progress":
        return "In Progress";
      case "completed":
        return "Completed";
      case "cancelled":
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex flex-col items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-sm px-4 md:px-8 py-4 flex items-center justify-between shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
              Talk to Expert
            </h1>
            {childName && (
              <p className="text-xs text-gray-500">
                Concerns for {childName}
              </p>
            )}
          </div>
          <div className="w-8" />
        </div>

        <div className="flex-1 px-4 md:px-6 lg:px-8 mt-20 pb-28">
          <div className="max-w-6xl mx-auto">
            {concerns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 px-4">
                <div className="w-64 h-64 mb-8 opacity-60">
                  <svg viewBox="0 0 200 200" className="w-full h-full">
                    <circle cx="100" cy="100" r="80" fill="#e0f2fe" />
                    <path d="M70 90 Q70 70 85 70 Q100 70 100 85 Q100 100 85 100 Q70 100 70 90" fill="#1e3a5f" />
                    <path d="M115 90 Q115 70 130 70 Q145 70 145 85 Q145 100 130 100 Q115 100 115 90" fill="#1e3a5f" />
                    <path d="M70 130 Q100 150 130 130" stroke="#1e3a5f" strokeWidth="4" fill="none" strokeLinecap="round" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-[#1e3a5f] mb-3">
                  No Concerns Yet
                </h3>
                <p className="text-gray-600 text-center mb-2 max-w-md text-lg">
                  You haven't raised any concerns yet.
                </p>
                <p className="text-gray-500 text-center max-w-md">
                  Click the button below to get expert guidance and support for your child's development.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
                {concerns.map((concern) => (
                  <div
                    key={concern.id}
                    onClick={() => handleCardClick(concern)}
                    className="group relative bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer border border-gray-200/60 hover:border-[#1e3a5f]/30 overflow-hidden transform hover:-translate-y-1"
                  >
                    {/* Top gradient accent */}
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#1e3a5f] via-[#2a4b73] to-[#3d5a8f] opacity-80 group-hover:opacity-100 transition-opacity"></div>

                    <div className="p-6">
                      {/* Header with status and date */}
                      <div className="flex items-start justify-between mb-5">
                        <div className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border-2 ${getStatusColor(concern.status)} shadow-sm`}>
                          <span className="flex-shrink-0">
                            {getStatusIcon(concern.status)}
                          </span>
                          <span className="text-xs font-bold tracking-wide uppercase">
                            {getStatusText(concern.status)}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs font-medium text-gray-400">
                            {new Date(concern.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="mb-6 min-h-[80px]">
                        <p className="text-sm leading-relaxed text-gray-700 line-clamp-4 group-hover:text-gray-900 transition-colors">
                          {concern.description}
                        </p>
                      </div>

                      {/* Footer with action button */}
                      <div className="flex items-center justify-between pt-5 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#2a4b73] flex items-center justify-center text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:scale-110">
                            →
                          </div>
                        </div>
                        <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#1e3a5f] bg-[#1e3a5f]/5 rounded-lg group-hover:bg-[#1e3a5f] group-hover:text-white transition-all duration-300">
                          <span>View Details</span>
                          <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Subtle background decoration */}
                    <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-gradient-to-br from-[#1e3a5f]/5 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md px-4 md:px-8 py-4 shadow-lg border-t border-gray-200">
          <div className="max-w-6xl mx-auto">
            <button
              onClick={() => setShowAddModal(true)}
              className="w-full py-4 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 text-lg"
            >
              <Plus className="w-6 h-6" />
              Raise New Concern
            </button>
          </div>
        </div>
      </div>

      {showAddModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseAddModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] px-6 py-5 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Raise New Concern</h2>
              <button
                onClick={handleCloseAddModal}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6 text-white" />
              </button>
            </div>

            <form onSubmit={handleSubmitConcern} className="p-6 md:p-8">
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Describe Your Concern *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-2xl focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none resize-none"
                  rows="6"
                  placeholder="Please describe your concern in detail. Our experts will review it and provide guidance..."
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleCloseAddModal}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-all"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-xl font-semibold shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={submitting || !formData.description.trim()}
                >
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Concern
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && selectedConcern && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] px-6 py-5 rounded-t-3xl flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Concern Details</h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6 text-white" />
              </button>
            </div>

            <div className="p-6 md:p-8">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${getStatusColor(selectedConcern.status)}`}>
                    {getStatusIcon(selectedConcern.status)}
                    <span className="text-sm font-semibold">
                      {getStatusText(selectedConcern.status)}
                    </span>
                  </div>
                  <span className="text-sm text-gray-500">
                    {new Date(selectedConcern.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Your Concern:</h4>
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                  <p className="text-gray-700 leading-relaxed">
                    {selectedConcern.description}
                  </p>
                </div>
              </div>

              {selectedConcern.status === "completed" && (
                <>
                  {selectedConcern.admin_response && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Expert Response:
                      </h4>
                      <div className="bg-green-50 rounded-2xl p-4 border border-green-100">
                        <p className="text-gray-700 leading-relaxed">
                          {selectedConcern.admin_response}
                        </p>
                        {selectedConcern.updated_at && (
                          <p className="text-xs text-gray-500 mt-3">
                            Responded on {new Date(selectedConcern.updated_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedConcern.expert && (
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">
                        Expert Details:
                      </h4>
                      <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-indigo-200 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div>
                            <h5 className="font-bold text-lg text-gray-800">
                              {selectedConcern.expert.name}
                            </h5>
                            <p className="text-sm text-indigo-600 font-semibold">
                              {selectedConcern.expert.speciality}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 mt-3">
                          {selectedConcern.expert.description}
                        </p>
                        <div className="mt-4 pt-3 border-t border-gray-100">
                          <p className="text-xs text-gray-500">
                            Contact: <span className="font-medium text-gray-700">{selectedConcern.expert.contact}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {selectedConcern.status === "pending" && (
                <div className="bg-yellow-50 rounded-2xl p-4 border border-yellow-100">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-yellow-800 mb-1">
                        Waiting for Expert Response
                      </p>
                      <p className="text-sm text-yellow-700">
                        Our experts are reviewing your concern. You'll receive a response soon.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedConcern.status === "in_progress" && (
                <div className="bg-blue-50 rounded-2xl p-4 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-800 mb-1">
                        In Progress
                      </p>
                      <p className="text-sm text-blue-700">
                        Our experts are working on your concern. We'll update you shortly.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedConcern.status === "cancelled" && (
                <div className="bg-red-50 rounded-2xl p-4 border border-red-100">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800 mb-1">
                        Request Cancelled
                      </p>
                      {selectedConcern.admin_response ? (
                        <p className="text-sm text-red-700">
                          Reason: {selectedConcern.admin_response}
                        </p>
                      ) : (
                        <p className="text-sm text-red-700">
                          This concern request has been cancelled.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 pb-6 md:px-8 md:pb-8">
              <button
                onClick={handleCloseModal}
                className="w-full py-3 bg-[#1e3a5f] text-white rounded-xl font-semibold shadow-md hover:bg-[#152e4a] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
